import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle } from "lucide-react";

const QuoteRequestPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { estimate, projectInfo } = location.state || {};
  
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    address: "",
    message: ""
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!estimate) {
      navigate('/');
    }
  }, [estimate, navigate]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    }
    
    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatSize = (size) => {
    const sizes = { small: "Small", medium: "Medium", large: "Large", xl: "Extra Large" };
    return sizes[size] || size;
  };

  const formatQuality = (quality) => {
    const qualities = { budget: "Budget", standard: "Standard", premium: "Premium" };
    return qualities[quality] || quality;
  };

  const formatComplexity = (complexity) => {
    const complexities = { simple: "Simple", moderate: "Moderate", complex: "Complex" };
    return complexities[complexity] || complexity;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setSubmitting(true);
    
    // Build WhatsApp message with estimate details
    const additionalMessage = formData.message ? `\n\n*Additional Notes:*\n${formData.message}` : '';
    
    const whatsappMessage = `*Quote Request from Website*

*Customer Details:*
Name: ${formData.name}
Phone: ${formData.phone}
Address: ${formData.address}

*Project Details:*
Type: ${projectInfo?.name || 'Not specified'}
Size: ${formatSize(estimate.room_size)}
Quality: ${formatQuality(estimate.quality_level)}
Complexity: ${formatComplexity(estimate.complexity)}

*Estimated Cost:* ${formatCurrency(estimate.total_estimate)}
*Timeline:* ${estimate.timeline_weeks} weeks

*Cost Breakdown:*
- Materials: ${formatCurrency(estimate.materials_cost)}
- Labour: ${formatCurrency(estimate.labour_cost)}
- Additional: ${formatCurrency(estimate.additional_cost)}${additionalMessage}`;

    // Encode the message for URL
    const encodedMessage = encodeURIComponent(whatsappMessage);
    const whatsappUrl = `https://wa.me/447780996775?text=${encodedMessage}`;
    
    // Open WhatsApp
    window.open(whatsappUrl, '_blank');
    
    setSubmitted(true);
    toast.success("Opening WhatsApp to send your quote request!");
    setSubmitting(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  if (!estimate) {
    return null;
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
          <title>Quote Request Sent | TJP Carpentry & Construction</title>
        </Helmet>
        <Header />
        <main className="max-w-2xl mx-auto px-6 py-20">
          <div className="text-center animate-fade-in" data-testid="quote-success-message">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-8">
              <CheckCircle className="text-green-600" size={40} />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              Quote Request Ready!
            </h1>
            <p className="text-lg text-stone-600 mb-8">
              WhatsApp should have opened with your quote details. If it did not open, 
              you can call us directly on <a href="tel:07780996775" className="text-amber-700 font-semibold">07780 996775</a>.
            </p>
            <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
              <p className="text-sm text-stone-500 mb-2">Your Estimate</p>
              <p className="text-3xl font-black text-slate-900">{formatCurrency(estimate.total_estimate)}</p>
              <p className="text-stone-600">{projectInfo?.name}</p>
            </div>
            <Button
              onClick={() => navigate('/')}
              className="bg-slate-900 text-white hover:bg-slate-800 h-12 px-8"
              data-testid="back-home-button"
            >
              Back to Home
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Request a Quote | TJP Carpentry & Construction</title>
      </Helmet>
      <Header />

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form Section */}
          <div className="lg:col-span-2">
            <div className="mb-8">
              <Button
                variant="ghost"
                onClick={() => navigate('/results', { state: { estimate, projectInfo } })}
                className="mb-4 -ml-4"
                data-testid="back-to-results-button"
              >
                <ArrowLeft className="mr-2" size={18} />
                Back to Results
              </Button>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-2">
                Request a Detailed Quote
              </h1>
              <p className="text-stone-600">
                Fill in your details and we will send your quote request via WhatsApp.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-xl shadow-stone-200/50 p-8">
              <div className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-slate-900 font-medium">
                    Full Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Smith"
                    className={`mt-2 h-12 ${errors.name ? 'border-red-500' : ''}`}
                    data-testid="name-input"
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>

                <div>
                  <Label htmlFor="phone" className="text-slate-900 font-medium">
                    Phone Number <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="07123 456789"
                    className={`mt-2 h-12 ${errors.phone ? 'border-red-500' : ''}`}
                    data-testid="phone-input"
                  />
                  {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                </div>

                <div>
                  <Label htmlFor="address" className="text-slate-900 font-medium">
                    Project Address <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="123 Main Street, London, SW1A 1AA"
                    className={`mt-2 h-12 ${errors.address ? 'border-red-500' : ''}`}
                    data-testid="address-input"
                  />
                  {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
                </div>

                <div>
                  <Label htmlFor="message" className="text-slate-900 font-medium">
                    Additional Details (Optional)
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us more about your project requirements, access details, or any specific concerns..."
                    className="mt-2 min-h-[120px]"
                    data-testid="message-input"
                  />
                </div>

                <WhatsAppButton
                  submitting={submitting}
                  data-testid="submit-quote-button"
                >
                  Send Quote Request via WhatsApp
                </WhatsAppButton>
              </div>
            </form>
          </div>

          {/* Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="bg-slate-900 text-white rounded-lg p-6 mb-4">
                <p className="text-stone-400 text-sm mb-1">Estimated Total</p>
                <p className="text-3xl font-black">{formatCurrency(estimate.total_estimate)}</p>
                <p className="text-stone-400 text-sm mt-1">{estimate.timeline_weeks} weeks timeline</p>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="font-bold text-slate-900 mb-4">Project Details</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-stone-500">Type</span>
                    <span className="font-medium text-slate-900">{projectInfo?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-500">Size</span>
                    <span className="font-medium text-slate-900 capitalize">{estimate.room_size}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-500">Quality</span>
                    <span className="font-medium text-slate-900 capitalize">{estimate.quality_level}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-500">Complexity</span>
                    <span className="font-medium text-slate-900 capitalize">{estimate.complexity}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default QuoteRequestPage;
